import { Component, Inject, Input, OnInit, forwardRef } from '@angular/core';
import { UIRouter }                        from '@uirouter/core';
import { TranslateService }               from '@ngx-translate/core';

import { ApiService }          from '../api/api.service';
import { BranchesLiteApiService } from '../branches/branches-lite.service';
import { CompanyService }      from '../companies/company.service';
import { ConfirmService }      from '../messages/confirm/confirm.service';
import { MessagesService }     from '../messages/messages.service';
import { MessengerService }    from '../messages/messenger/messenger.service';
import { PartstechService }    from '../partstech/partstech.service';
import { SessionService }      from '../sessions/session.service';
import { StreamSearchService } from '../api/stream-search.service';
import { UKPostcodeService }   from '../uk-postcode/uk-postcode.service';
import { VendorFactoryService } from './vendor-factory.service';

import VendorSearchResultsHtml from './templates/vendors.search.results.html';

@Component({
  selector   : 'wor-vendor',
  template: require('./vendor.component.html')
})
export class VendorComponent implements OnInit {
  @Input() billedVendors: any[];
  @Input() companyMessages: any;
  @Input() invoices: any[];
  @Input() orders: any[];
  @Input() payments: any[];
  @Input() paymentTerms: any[];
  @Input() vendor: any;

  billerSearch: any;
  addressSearch: any;
  branchInvoices: any[] = [];
  branchPayments: any[] = [];
  countryTypes: any[];
  currency: any;
  loading = false;
  session: any;
  states: any[];
  validations: any;
  vendorObj: any;

  constructor (
    @Inject(forwardRef(() => BranchesLiteApiService)) private branchesApi: BranchesLiteApiService,
    @Inject(forwardRef(() => PartstechService)) private partstechApi: PartstechService,
    @Inject(forwardRef(() => UKPostcodeService)) private ukPostcodeApi: UKPostcodeService,
    @Inject(forwardRef(() => ApiService)) private apiService: ApiService,
    @Inject(forwardRef(() => CompanyService))
    public companyService: CompanyService,
    @Inject(forwardRef(() => ConfirmService)) private confirmService: ConfirmService,
    @Inject(forwardRef(() => MessagesService)) private messagesService: MessagesService,
    @Inject(forwardRef(() => MessengerService)) private messengerService: MessengerService,
    private router: UIRouter,
    @Inject(forwardRef(() => SessionService))
    private sessionService: SessionService,
    @Inject(forwardRef(() => StreamSearchService)) private streamSearchService: StreamSearchService,
    @Inject(forwardRef(() => VendorFactoryService))
    private vendorFactoryService: VendorFactoryService,
    private translateService: TranslateService
  ) {}

  checkRights (module: string): boolean { return this.sessionService.hasRights(module); }

  ngOnInit () {
    this.session = this.sessionService.base();
    this.currency = this.sessionService.currency();
    this.vendorObj = this.vendorFactoryService.create(this.vendor);

    this.addressSearch = {
      list: [],
      selected: { full_address: '', id: '' },
      search: (typed) => this.apiService.get('/NzPostal/get_addresses', typed)
        .then(addresses => addresses !== 'null' ? addresses : ''),
      choose: (address) => {
        this.loading = true;
        return this.apiService.get('/NzPostal/get_address_details', address.address_id)
        .then(details => {
          this.vendor.address1 = details.address1;
          this.vendor.address2 = details.address2;
          this.vendor.country_code = details.country_code;
          this.vendor.latitude = details.latitude;
          this.vendor.longitude = details.longitude;
          this.vendor.postcode = details.postcode;
          this.vendor.state = details.state;
          this.vendor.suburb = details.suburb;
        }).finally(() => this.loading = false);
      },
      format: () => typeof this.vendor.address1 === 'string' ? this.vendor.address1 : ''
    };

    this.billerSearch = {
      list: [],
      selected: null as any,
      templateUrl: VendorSearchResultsHtml,
      search: (typed) => this.streamSearchService.search('vendor_billers', typed)
        .then(vendors => this.billerSearch.list.concat(vendors)),
      choose: (vendor) => {
        this.vendor.biller_id = vendor.id;
        this.vendor.biller_name = vendor.company_name;
      },
      format: (vendor) => vendor ? vendor.company_name : ''
    };

    if (this.vendorObj.isBranchVendor()) {
      this.branchesApi.getVendorPayments(this.vendor)
        .then(payments => this.branchPayments = payments)
        .catch(err => this.messagesService.error(err));

      this.branchesApi.getVendorInvoices(this.vendor)
        .then(invoices => this.branchInvoices = invoices)
        .catch(err => this.messagesService.error(err));
    }

    if (this.vendor.country_code) {
      this.setCountryDefaults();
    } else {
      this.apiService.get('/system/state_types/', null, 'states').then(states => {
        this.states = states;
        this.validations = this.sessionService.countryValidation();
      });
    }

    this.apiService.get('/system/country_types/', null, 'country_types').then(types => this.countryTypes = types);

    if (this.vendor.biller_id) {
      this.getBiller();
    }
  }

  autolink () {
    this.apiService.get('/vendors/autolink_to_branch', this.vendor.id)
    .then(count => this.messagesService.show(`Autolink Created! ${count} Record(s) Linked.`, 'success'))
    .catch(err => this.messagesService.error(err));
  }

  delete () {
    this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_SUPPLIER'))
    .then(() => this.apiService.delete('/vendor', this.vendor.id))
    .then(() => {
      this.router.stateService.go('app.vendors');
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.DELETED.DELETED'), 'success');
    })
    .catch(err => this.messagesService.error(err));
  }

  emailStatement () {
    this.vendorObj.emailStatement?.()
    ?.then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.EMAIL_SENT'), 'success'));
  }

  getBiller () {
    this.apiService.get('/vendor', this.vendor.biller_id).then(biller => {
      this.billerSearch.selected = biller;
      this.vendor.biller_name = biller.company_name;
    }).catch(err => this.messagesService.error(err));
  }

  isPartstechVendor () {
    return this.partstechApi.isPartstechVendor(this.vendor);
  }

  manuallyLinkVendor () {
    return this.vendorObj.manuallyLinkVendor()
    .then(() => {
      this.messagesService.show('Vendor Linked', 'success');
      this.router.stateService.reload();
    });
  }

  save () {
    if (!this.billerSearch.selected) {
      this.vendor.biller_id = null;
    }

    this.vendorObj.save().then(vendor => {
      this.vendor = vendor;
      this.vendorObj = this.vendorFactoryService.create(this.vendor);

      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.SUPPLIER'), 'success');

      this.vendor.id
        ? this.router.stateService.reload()
        : this.router.stateService.go('app.vendor', { id: vendor.id });
    }).catch(err => this.messagesService.error(err));
  }

  searchUKPostcode () {
    this.ukPostcodeApi.search(this.vendor.postcode)
    .then(data => {
      this.vendor.address1 = data.Premises;
      this.vendor.address2 = data.Street;
      this.vendor.suburb = data.PostTown;
      this.vendor.postcode = data.Postcode;
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.UPDATED.SUPPLIER_ADDRESS'), 'success');
    })
    .catch(err => this.messagesService.error(err));
  }

  sendEmail () {
    this.messengerService.email({
      name: this.vendor.company_name,
      email_address: this.vendor.email,
      body: this.companyMessages?.supplier_email_message,
      attachment_type: 5,
      vendor_id: this.vendor.id
    }).then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT'), 'success'));
  }

  setCountryDefaults () {
    this.apiService.get('/system/states_for_country', this.vendor.country_code).then(states => {
      this.states = states;
      return this.apiService.get('/system/country_validations', this.vendor.country_code);
    }).then(validations => {
      this.validations = validations;
    }).catch(err => this.messagesService.error(err));
  }

  syncWithHeadOffice () {
    Promise.resolve()
    .then(() => this.vendorObj.save())
    .then(() => this.apiService.patch('/vendors/sync_multibranch_vendor_entry', this.vendor))
    .then(() => {
      this.messagesService.show(this.translateService.instant('MULTI_BRANCH_SPACE.VENDOR_LINKED'), 'success');
      this.router.stateService.reload();
    })
    .catch(err => this.messagesService.error(err));
  }
}
